import { createContext, useContext, useState, useEffect } from 'react';
import api, { sendToLogin } from '@/lib/api';
import {
  ACCESS_TOKEN_KEY,
  AUTH_EPOCH_KEY,
  cleanupLegacyAuthKeys,
  clearTokens,
  currentAuthEpoch,
  getAccessToken,
  getRefreshToken,
  replaceTokens,
  revokeSession,
} from '@/lib/authTokens';
import { startSessionTimer, clearSessionTimer } from '@/components/SessionTimeoutBanner';

const AuthContext = createContext(null);

function rememberTheme(userData) {
  if (userData?.user_id && userData?.theme_preference) {
    try {
      localStorage.setItem(`theme_${userData.user_id}`, userData.theme_preference);
    } catch {
      /* storage unavailable */
    }
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    // A session left by the Supabase-era frontend cannot be used any more: say so on signed-in pages
    // (/login?expired=1), exactly as for a session that ended on the server.
    if (cleanupLegacyAuthKeys()) sendToLogin();

    const initAuth = async () => {
      // A sign-in, sign-up or sign-out while this check is in flight moves the epoch; its answer (or failure)
      // then describes the previous session and must not replace the newer one.
      const epoch = currentAuthEpoch();
      try {
        if (getAccessToken() || getRefreshToken()) {
          // The api client attaches the token and refreshes it first if it is about to expire.
          const res = await api.get('/api/auth/me');
          if (mounted && currentAuthEpoch() === epoch) {
            rememberTheme(res.data);
            setUser(res.data);
          }
        }
      } catch {
        if (mounted && currentAuthEpoch() === epoch) setUser(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    initAuth();

    // Follow other tabs: signing out there signs this tab out; signing in there (possibly as someone else, or
    // after a password change) moves the epoch, and this tab re-reads who it is now.
    const onStorage = (event) => {
      if (!mounted) return;
      if (event.key === ACCESS_TOKEN_KEY && !event.newValue) {
        setUser(null);
      } else if (event.key === AUTH_EPOCH_KEY) {
        if (!getAccessToken() && !getRefreshToken()) {
          setUser(null);
          return;
        }
        const epoch = currentAuthEpoch();
        api.get('/api/auth/me')
          .then((res) => {
            if (!mounted || currentAuthEpoch() !== epoch) return;
            rememberTheme(res.data);
            setUser(res.data);
          })
          .catch(() => {}); // a dead session is handled by the api client; a network error keeps the user
      }
    };
    window.addEventListener('storage', onStorage);
    return () => {
      mounted = false;
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const login = async (email, password, turnstile_token = null) => {
    const res = await api.post('/api/auth/login', { email, password, turnstile_token });
    const { user: userData } = res.data;
    replaceTokens(res.data);
    // Store theme preference BEFORE setting user state so ThemeContext reads it synchronously
    rememberTheme(userData);
    setUser(userData);
    // Start the 24-hour session timer (drives idle warning + auto-logout)
    startSessionTimer();
    return userData;
  };

  const register = async (regData) => {
    const res = await api.post('/api/auth/register', regData);
    const { user: userData, invitation_job_id: invitationJobId } = res.data;
    replaceTokens(res.data);
    setUser(userData);
    startSessionTimer();
    return { ...userData, invitation_job_id: invitationJobId || null };
  };

  const logout = async () => {
    // Forget everything locally FIRST (synchronously): an idle-timeout logout must not be undone by a refresh that
    // is still in flight, and clearTokens() makes any such refresh discard its result.
    const accessToken = getAccessToken();
    const refreshToken = getRefreshToken();
    clearTokens();
    clearSessionTimer();
    setUser(null);
    revokeSession(accessToken, refreshToken);
  };

  const updateUser = (data) => {
    setUser((prev) => (prev ? { ...prev, ...data } : data));
  };

  const refreshUser = async () => {
    if (!getAccessToken() && !getRefreshToken()) return null;
    const epoch = currentAuthEpoch();
    try {
      const res = await api.get('/api/auth/me');
      if (currentAuthEpoch() !== epoch) return null; // another account signed in meanwhile
      setUser(res.data);
      return res.data;
    } catch {
      return null;
    }
  };


  const token = getAccessToken();

  return (
    <AuthContext.Provider
      value={{ user, loading, token, login, register, logout, updateUser, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
};
